import { Layer, ZIndexes } from "@common/constants";
import { type HazardName, hazardOfSky } from "@common/definitions/hazards";
import { Night } from "@common/definitions/night";
import { mapWeather, type SkyName } from "@common/utils/mapWeather";
import { Numeric } from "@common/utils/math";
import { Vec, type Vector } from "@common/utils/vector";
import { Container, type Sprite } from "pixi.js";
import { Game } from "../game";
import { PIXI_SCALE } from "../utils/constants";
import { TrenchioSprite } from "../utils/pixi";
import { CameraManager } from "./cameraManager";
import { GasManager } from "./gasManager";
import { MapManager } from "./mapManager";
import { NightLight } from "./nightLight";
import { ParticleManager } from "./particleManager";
import { type GameSound, SoundManager } from "./soundManager";

/*
    The hazard that closes in (0.98.4, definitions/hazards.ts; the owner, 31 October 2026): the gas's zone drawn as a
    dust storm at dusk ("a well designed, nice dust storm": a towering wall of tan dust churning along the edge, dust
    streaming inside it, tumbleweeds rolling along its foot, the light going amber as it nears, and inside it a few
    steps of sight) and as a wildfire at night ("same damage as gas, just different visual": a line of fire along the
    edge drawn with the game's own flames, embers rising and smoke drifting with the wind, the ground behind it black
    and scorched, and its light on the night's dark). The zone itself, where it is and what it does, is the gas's
    (managers/gasManager.ts: the server's circle); this draws the hazard's look along that circle's edge on screen.

    The gas's plane (GasRender) still covers the zone, in the hazard's colour (`hazardPlane`); this adds the edge and
    what's inside, in pooled sprites placed along the arc of the circle the screen shows, each frame.
*/

/** the plane over the zone: the gas's own colour under the gas; the dust's tan and the fire's scorched black otherwise */
export const hazardPlane = (hazard: HazardName): { readonly color: number, readonly alpha: number } | undefined => {
    switch (hazard) {
        case "dust": return { color: 0xb8905a, alpha: 0.6 };
        case "wildfire": return { color: 0x1a0a05, alpha: 0.72 };
        default: return undefined;
    }
};

/** the minimap's plane and ring for each hazard (the gas keeps its own colour) */
export const hazardMapColor = (hazard: HazardName): number | undefined => {
    switch (hazard) {
        case "dust": return 0xc9a46a;
        case "wildfire": return 0xff6a1a;
        default: return undefined;
    }
};

/** the zone's container: over the gas's plane (1000), under the night's HUD words */
const HAZARD_ZINDEX = 1001;
/** how far apart the sprites along the edge stand (units of arc), and how many at most */
const EDGE_STEP = 5.5;
const MAX_EDGE = 150;
/** inside the zone, how many loose things at most (dust streaks, flames) */
const MAX_INSIDE = 90;
/** the wall's dust reaches this far out of the zone (the front rolling ahead of the edge) and this far in */
const DUST_OUT = 9;
const DUST_IN = 26;
/** the fire's flames stand this far either side of the edge */
const FIRE_OUT = 2;
const FIRE_IN = 14;
/** the fire's light on the dark: lamps along the edge this far apart, each this wide */
const FIRE_LAMP_STEP = 22;
const FIRE_LAMP_RADIUS = 24;
const MAX_LAMPS = 24;

const FLAME_FRAMES = ["fire_flame_1", "fire_flame_2", "fire_flame_3", "fire_flame_4"];
/** the world's grid of cells the loose things inside the zone keep to (units): a streak of dust, a scorch mark, a small fire */
const INSIDE_CELL = 26;

/** a number 0 to 1 that belongs to a cell of the world (the same every frame: nothing in rows, nothing swapping about) */
const cellHash = (column: number, row: number, salt = 0): number => {
    const n = Math.sin(column * 127.1 + row * 311.7 + salt * 74.7) * 43758.5453;
    return n - Math.floor(n);
};

/**
 * a sheet over the screen, under the HUD: put into #game before #game-ui, so it paints over the canvas (a plain block,
 * painted first) and under the HUD's boxes (positioned after it in the page)
 */
const underHud = (sheet: HTMLElement): void => {
    const game = document.getElementById("game");
    if (game) game.insertBefore(sheet, game.firstChild);
    else document.body.appendChild(sheet);
};

interface Edge {
    readonly sprite: Sprite
    seed: number
}

class HazardRenderClass {
    private _hazard: HazardName = "gas";
    private _layer?: Container;
    private readonly _edge: Edge[] = [];
    private readonly _inside: Edge[] = [];
    private readonly _lampOwners: object[] = [];
    private _tumbleweeds: Array<{ sprite: Sprite, along: number, speed: number, spin: number, born: number }> = [];
    private _wind = Vec(1, 0);
    private _windSeed = -1;
    private _lastEmber = 0;
    private _lastDust = 0;
    private _roar?: GameSound;
    private _wind_sound?: GameSound;
    private _amber?: HTMLDivElement;
    private _amberShown = "";
    private _insideDust?: HTMLDivElement;

    get hazard(): HazardName { return this._hazard; }

    /** the map packet came, under this sky: the hazard it brings */
    begin(sky: SkyName): void {
        this.reset();
        this._hazard = hazardOfSky(sky);
    }

    /** the zone's container, over the world's floors (made once a match) */
    private _container(): Container {
        if (this._layer) return this._layer;
        const layer = new Container();
        layer.zIndex = HAZARD_ZINDEX;
        layer.eventMode = "none";
        layer.sortableChildren = true;
        CameraManager.addObject(layer);
        this._layer = layer;
        return layer;
    }

    /** the screen's edges, world units: the circle's arc inside them is drawn */
    private _view(): { readonly min: Vector, readonly max: Vector } {
        const scale = CameraManager.container.scale.x || 1;
        const halfW = CameraManager.width / scale / 2 / PIXI_SCALE;
        const halfH = CameraManager.height / scale / 2 / PIXI_SCALE;
        const center = Vec.scale(CameraManager.position, 1 / PIXI_SCALE);
        return { min: Vec(center.x - halfW, center.y - halfH), max: Vec(center.x + halfW, center.y + halfH) };
    }

    /**
     * the points along the circle's edge the screen shows, `step` apart along the arc, each with its outward angle: the
     * arcs of the circle inside the screen (widened by `margin`), found from where the circle crosses the screen's
     * edges (`circleArcs`), never more than `limit`
     */
    private _arc(center: Vector, radius: number, step: number, margin: number, limit: number): Array<{ readonly point: Vector, readonly angle: number }> {
        const out: Array<{ readonly point: Vector, readonly angle: number }> = [];
        if (radius <= 1) return out;
        const view = this._view();
        const min = Vec(view.min.x - margin, view.min.y - margin), max = Vec(view.max.x + margin, view.max.y + margin);
        const dAngle = step / radius;
        for (const [from, to] of circleArcs(center, radius, min, max)) {
            for (let angle = from; angle < to && out.length < limit; angle += dAngle) {
                out.push({ point: Vec(center.x + Math.cos(angle) * radius, center.y + Math.sin(angle) * radius), angle });
            }
        }
        return out;
    }

    private _sprite(pool: Edge[], index: number, frame: string, blend: "normal" | "add" = "normal"): Edge {
        let edge = pool[index];
        if (!edge) {
            const sprite = new TrenchioSprite(frame);
            sprite.blendMode = blend;
            this._container().addChild(sprite);
            edge = { sprite, seed: Math.random() * 1000 };
            pool.push(edge);
        }
        edge.sprite.visible = true;
        return edge;
    }

    private _hideFrom(pool: Edge[], index: number): void {
        for (let i = index; i < pool.length; i++) pool[i].sprite.visible = false;
    }

    /** every frame, after the gas's plane has moved: the hazard's edge and inside along the circle on screen */
    update(): void {
        if (!Game.gameStarted || this._hazard === "gas") {
            this._hideAll();
            return;
        }
        const gasRender = Game.gasRender;
        const center = gasRender.center;
        const radius = gasRender.radius;
        if (MapManager.seed !== this._windSeed) {
            this._windSeed = MapManager.seed;
            const weather = mapWeather(MapManager.seed, this._hazard === "dust" ? "dusk" : "night");
            this._wind = Vec(Math.cos(weather.windAngle) * weather.windSpeed, Math.sin(weather.windAngle) * weather.windSpeed);
        }
        const now = Date.now();
        // (the frame's own time, so the tumbleweeds roll at one speed at any frame rate: 0.98.5's audit)
        this._frame = this._lastFrame === 0 ? 1 / 60 : Math.min(0.1, (now - this._lastFrame) / 1000);
        this._lastFrame = now;
        if (this._hazard === "dust") this._updateDust(center, radius, now);
        else this._updateFire(center, radius, now);
    }

    private _lastFrame = 0;
    /** this frame's length (s) */
    private _frame = 1 / 60;

    // ---------------- the dust storm (dusk) ----------------

    private _updateDust(center: Vector, radius: number, now: number): void {
        const t = now / 1000;
        const arc = this._arc(center, radius, EDGE_STEP, DUST_IN, MAX_EDGE);
        let used = 0;
        // the wall: three rows of churning puffs, the outer rolling ahead of the edge, the inner deep in it
        for (const { point, angle } of arc) {
            for (let row = 0; row < 3 && used < MAX_EDGE * 3; row++) {
                const edge = this._sprite(this._edge, used++, row === 1 ? "dust_puff_2" : "dust_puff_1");
                const s = edge.seed;
                const churn = Math.sin(t * 0.9 + s) * 2.2 + Math.cos(t * 0.6 + s * 1.7) * 1.4;
                const depth = row === 0 ? -DUST_OUT * 0.55 + churn : row === 1 ? 3 + churn : 10 + churn * 1.3;
                const along = Math.sin(t * 0.35 + s * 2.3) * 1.6;
                const x = point.x + Math.cos(angle) * depth - Math.sin(angle) * along;
                const y = point.y + Math.sin(angle) * depth + Math.cos(angle) * along;
                const sprite = edge.sprite;
                sprite.position.set(x * PIXI_SCALE, y * PIXI_SCALE);
                sprite.rotation = s + t * (0.22 + (s % 0.3)) * (row % 2 === 0 ? 1 : -1);
                const size = (row === 0 ? 9 : row === 1 ? 11 : 13) * (1 + 0.12 * Math.sin(t * 1.3 + s));
                sprite.scale.set((size * PIXI_SCALE) / 160);
                sprite.alpha = (row === 0 ? 0.5 : row === 1 ? 0.68 : 0.78) * (0.85 + 0.15 * Math.sin(t * 2.1 + s * 3));
                sprite.tint = row === 0 ? 0xd2b482 : row === 1 ? 0xc49f6b : 0xb08a58;
                sprite.zIndex = row;
            }
        }
        this._hideFrom(this._edge, used);

        // the dust streaming inside it, past the wall: long faint streaks with the wind, where the screen shows the zone.
        // Each streak keeps to a cell of a grid that slides with the wind (the cell's own hash sets it in its cell and
        // whether there's one at all), so the dust streams steadily and nothing stands in rows
        const view = this._view();
        const windAngle = Math.atan2(this._wind.y, this._wind.x);
        let streaks = 0;
        const slideX = t * this._wind.x * 4, slideY = t * this._wind.y * 4;
        const c0 = Math.floor((view.min.x - slideX) / INSIDE_CELL) - 1, c1 = Math.floor((view.max.x - slideX) / INSIDE_CELL) + 1;
        const r0 = Math.floor((view.min.y - slideY) / INSIDE_CELL) - 1, r1 = Math.floor((view.max.y - slideY) / INSIDE_CELL) + 1;
        for (let i = c0; i <= c1 && streaks < MAX_INSIDE; i++) {
            for (let j = r0; j <= r1 && streaks < MAX_INSIDE; j++) {
                const u = cellHash(i, j), v = cellHash(i, j, 1), w = cellHash(i, j, 2);
                if (u > 0.6) continue;
                const x = (i + v) * INSIDE_CELL + slideX, y = (j + w) * INSIDE_CELL + slideY;
                const into = Vec.len(Vec.sub(Vec(x, y), center)) - radius;
                if (into < 2) continue;
                const edge = this._sprite(this._inside, streaks++, "dust_haze");
                const sprite = edge.sprite;
                sprite.position.set(x * PIXI_SCALE, y * PIXI_SCALE);
                sprite.rotation = windAngle + Math.sin(t * 0.7 + u * 9) * 0.12;
                const length = 18 + 16 * u;
                sprite.scale.set((length * PIXI_SCALE) / 256, ((5 + 5 * v) * PIXI_SCALE) / 256);
                sprite.alpha = (0.08 + 0.08 * w) * Math.min(1, into / 20) * (0.7 + 0.3 * Math.sin(t * 1.7 + u * 20));
                sprite.tint = 0xd9bd8c;
                sprite.zIndex = 4;
            }
        }
        this._hideFrom(this._inside, streaks);

        // tumbleweeds rolling along the foot of the wall, now and then
        if (now - this._lastDust > 2600 && arc.length && this._tumbleweeds.length < 4) {
            this._lastDust = now;
            const at = arc[Math.floor(Math.random() * arc.length)];
            const sprite = new TrenchioSprite(Math.random() < 0.5 ? "tumbleweed_1" : "tumbleweed_2");
            sprite.zIndex = 3;
            this._container().addChild(sprite);
            this._tumbleweeds.push({ sprite, along: at.angle, speed: (Math.random() < 0.5 ? -1 : 1) * (0.9 + Math.random() * 0.8), spin: Math.random() * 6, born: now });
        }
        for (const weed of [...this._tumbleweeds]) {
            const age = (now - weed.born) / 1000;
            if (age > 9) {
                weed.sprite.destroy();
                this._tumbleweeds.splice(this._tumbleweeds.indexOf(weed), 1);
                continue;
            }
            weed.along += (weed.speed * 7 / Math.max(radius, 1)) * this._frame;
            const bounce = Math.abs(Math.sin(age * 4.5)) * 1.6;
            const depth = -3 - bounce;
            const x = center.x + Math.cos(weed.along) * (radius + depth);
            const y = center.y + Math.sin(weed.along) * (radius + depth);
            weed.sprite.position.set(x * PIXI_SCALE, y * PIXI_SCALE);
            weed.sprite.rotation = weed.spin + age * 5 * Math.sign(weed.speed);
            weed.sprite.scale.set((3.2 * PIXI_SCALE) / 64);
            weed.sprite.alpha = Math.min(1, age * 2, (9 - age));
        }

        // the amber light ahead of the wall, and the dust round you inside it: by how far you are from the edge
        const me = Game.activePlayer;
        const toEdge = me ? Vec.len(Vec.sub(me.position, center)) - radius : -Infinity;
        this._amberLight(toEdge, now);
        this._wall(now, toEdge);
    }

    /** the light turns amber as the wall nears (within 70 units), and the dust closes round you inside it */
    private _amberLight(toEdge: number, now: number): void {
        const near = Numeric.clamp(1 - Math.abs(Math.min(0, toEdge + 70)) / 70, 0, 1) * (toEdge < -70 ? 0 : 1);
        const insideShare = Numeric.clamp(toEdge / 14, 0, 1);
        const amber = Math.max(near * 0.22, insideShare * 0.55);
        const shown = amber.toFixed(2);
        if (shown !== this._amberShown) {
            this._amberShown = shown;
            if (!this._amber) {
                this._amber = document.createElement("div");
                this._amber.id = "dust-light";
                underHud(this._amber);
            }
            this._amber.style.opacity = shown;
            this._amber.classList.toggle("on", amber > 0.005);
        }
        // the wind, heard as the wall nears and roaring inside it
        const volume = Math.max(near * 0.5, insideShare);
        if (volume > 0.02) {
            this._wind_sound ??= SoundManager.play("dust_wind", { loop: true, ambient: true, dynamic: true, layer: Layer.Ground, falloff: 0, maxRange: 1, volume });
            this._wind_sound.volume = volume;
        } else if (this._wind_sound) {
            this._wind_sound.stop();
            this._wind_sound = undefined;
        }
    }

    /**
     * inside the wall, the dust round you: the screen under a sheet of it (`#dust-inside`, over the world and under the
     * HUD), clear for a few steps round you in the middle (you're always in the middle of the screen) and thick beyond,
     * the clear bubble shrinking as you go deeper
     */
    private _wall(now: number, toEdge: number): void {
        const me = Game.activePlayer;
        const on = me !== undefined && toEdge >= -2;
        if (!on) {
            if (this._insideDust) { this._insideDust.style.opacity = "0"; this._insideDust.classList.remove("on"); }
            return;
        }
        if (!this._insideDust) {
            this._insideDust = document.createElement("div");
            this._insideDust.id = "dust-inside";
            underHud(this._insideDust);
        }
        const t = now / 1000;
        const sheet = this._insideDust;
        // (the clear bubble: 11 units round you at the edge, 6 well inside; thick dust from twice that out)
        const share = Numeric.clamp(toEdge / 30, 0, 1);
        const scale = (CameraManager.container.scale.x || 1) * PIXI_SCALE;
        const clear = (11 - 5 * share) * scale * (1 + 0.04 * Math.sin(t * 1.9));
        sheet.style.setProperty("--clear", `${clear.toFixed(0)}px`);
        sheet.style.setProperty("--thick", `${(clear * 2.2).toFixed(0)}px`);
        sheet.style.opacity = (0.6 + 0.35 * share).toFixed(2);
        sheet.classList.add("on");
    }

    // ---------------- the wildfire (night) ----------------

    private _updateFire(center: Vector, radius: number, now: number): void {
        const t = now / 1000;
        const arc = this._arc(center, radius, EDGE_STEP * 0.6, FIRE_IN, MAX_EDGE);
        const lean = Math.max(-0.3, Math.min(0.3, this._wind.x * 0.15));
        let used = 0;
        // the line of fire: two rows of flames along the edge and a glow under them
        for (const { point, angle } of arc) {
            for (let row = 0; row < 2 && used < MAX_EDGE * 2; row++) {
                const edge = this._sprite(this._edge, used++, FLAME_FRAMES[0], "add");
                const s = edge.seed;
                const depth = row === 0 ? FIRE_OUT - 1 + Math.sin(t * 1.1 + s) * 1.2 : 5 + Math.sin(t * 0.8 + s * 2) * 2.5;
                const along = Math.sin(t * 0.5 + s * 3) * 1.4;
                const x = point.x + Math.cos(angle) * depth - Math.sin(angle) * along;
                const y = point.y + Math.sin(angle) * depth + Math.cos(angle) * along;
                const sprite = edge.sprite;
                sprite.texture = TrenchioSprite.getTexture(FLAME_FRAMES[Math.floor(t * 11 + s * 7) % FLAME_FRAMES.length]);
                sprite.anchor.set(0.5, 0.9);
                sprite.position.set(x * PIXI_SCALE, y * PIXI_SCALE);
                sprite.rotation = lean + Math.sin(t * 3 + s) * 0.06;
                const size = (row === 0 ? 0.95 : 1.45) * (0.85 + 0.25 * Math.sin(t * 2.6 + s * 4));
                sprite.scale.set(size * 0.9, size);
                sprite.alpha = row === 0 ? 0.85 : 0.9;
                // (the outer row orange, the fire's front; the inner tall and pale, its heart)
                sprite.tint = row === 0 ? 0xffa050 : 0xffe8c0;
                sprite.zIndex = 2 + row;
            }
        }
        this._hideFrom(this._edge, used);

        // behind the line, the burnt ground: scorch marks and small fires dotted inside the zone where the screen shows
        // it, each keeping to a cell of the world's grid (the cell's hash sets it in its cell, and whether there's one)
        const view = this._view();
        let inside = 0;
        const c0 = Math.floor(view.min.x / INSIDE_CELL), c1 = Math.floor(view.max.x / INSIDE_CELL);
        const r0 = Math.floor(view.min.y / INSIDE_CELL), r1 = Math.floor(view.max.y / INSIDE_CELL);
        for (let i = c0; i <= c1 && inside < MAX_INSIDE; i++) {
            for (let j = r0; j <= r1 && inside < MAX_INSIDE; j++) {
                const u = cellHash(i, j), v = cellHash(i, j, 1), w = cellHash(i, j, 2);
                if (u > 0.7) continue;
                const cx = (i + v) * INSIDE_CELL, cy = (j + w) * INSIDE_CELL;
                const into = Vec.len(Vec.sub(Vec(cx, cy), center)) - radius;
                if (into < 4) continue;
                const edge = this._sprite(this._inside, inside++, "fire_scorch_1");
                const sprite = edge.sprite;
                const flame = u < 0.22 && into < 60;
                if (flame) {
                    sprite.texture = TrenchioSprite.getTexture(FLAME_FRAMES[Math.floor(t * 9 + u * 50) % FLAME_FRAMES.length]);
                    sprite.blendMode = "add";
                    sprite.anchor.set(0.5, 0.9);
                    sprite.rotation = lean;
                    const size = 0.55 + 0.3 * v + 0.2 * Math.sin(t * 2.2 + u * 30);
                    sprite.scale.set(size * 0.9, size);
                    sprite.alpha = 0.85;
                    sprite.tint = 0xffd090;
                } else {
                    sprite.texture = TrenchioSprite.getTexture(`fire_scorch_${1 + Math.floor(v * 3)}`);
                    sprite.blendMode = "normal";
                    sprite.anchor.set(0.5);
                    sprite.rotation = u * 6.28;
                    sprite.scale.set(1.1 + 0.7 * w);
                    sprite.alpha = 0.42;
                    sprite.tint = 0x2a1a10;
                }
                sprite.position.set(cx * PIXI_SCALE, cy * PIXI_SCALE);
                sprite.zIndex = flame ? 2 : 0;
            }
        }
        this._hideFrom(this._inside, inside);

        // embers rising from the line and smoke drifting with the wind
        if (arc.length && now - this._lastEmber > 70) {
            this._lastEmber = now;
            for (let k = 0; k < 3; k++) {
                const at = arc[Math.floor(Math.random() * arc.length)];
                const depth = 1 + Math.random() * 8;
                const position = Vec(at.point.x + Math.cos(at.angle) * depth, at.point.y + Math.sin(at.angle) * depth);
                ParticleManager.spawnParticle({
                    frames: "fire_ember",
                    position,
                    lifetime: 900 + Math.random() * 900,
                    zIndex: ZIndexes.Gas,
                    layer: Layer.Ground,
                    scale: { start: 0.5 + Math.random() * 0.5, end: 0.1 },
                    alpha: { start: 1, end: 0 },
                    emissive: true,
                    speed: Vec(this._wind.x * 2 + (Math.random() - 0.5) * 3, this._wind.y * 2 - 3 - Math.random() * 3)
                });
            }
            if (Math.random() < 0.5) {
                const at = arc[Math.floor(Math.random() * arc.length)];
                const depth = 2 + Math.random() * 10;
                ParticleManager.spawnParticle({
                    frames: "smoke_particle",
                    position: Vec(at.point.x + Math.cos(at.angle) * depth, at.point.y + Math.sin(at.angle) * depth),
                    lifetime: 2600 + Math.random() * 1600,
                    zIndex: ZIndexes.Gas,
                    layer: Layer.Ground,
                    scale: { start: 1.4 + Math.random() * 0.6, end: 4.2 + Math.random() * 1.5 },
                    alpha: { start: 0.34, end: 0 },
                    rotation: { start: Math.random() * 6.28, end: Math.random() * 6.28 },
                    tint: 0x2b2622,
                    speed: Vec(this._wind.x * 2.6 + (Math.random() - 0.5), this._wind.y * 2.6 - 1.4)
                });
            }
        }

        // the fire's light on the night's dark: a warm pool every so often along the edge
        const lampArc = this._arc(center, radius, FIRE_LAMP_STEP, FIRE_LAMP_RADIUS, MAX_LAMPS);
        for (let i = 0; i < MAX_LAMPS; i++) {
            const owner = this._lampOwners[i] ??= {};
            const at = lampArc[i];
            if (!at) { NightLight.setLamp(owner, undefined); continue; }
            const depth = 4;
            NightLight.setLamp(owner, { x: at.point.x + Math.cos(at.angle) * depth, y: at.point.y + Math.sin(at.angle) * depth, layer: Layer.Ground, radius: FIRE_LAMP_RADIUS * (0.9 + 0.1 * Math.sin(t * 3 + i)) });
        }

        // the roar, near the line and inside it; the screen's edges glow with fire inside it (the fire's own vignette)
        const me = Game.activePlayer;
        const toEdge = me ? Vec.len(Vec.sub(me.position, center)) - radius : -Infinity;
        const near = Numeric.clamp(1 - Math.abs(Math.min(0, toEdge + 50)) / 50, 0, 1) * (toEdge < -50 ? 0 : 1);
        const insideShare = Numeric.clamp(toEdge / 10, 0, 1);
        const volume = Math.max(near * 0.6, insideShare);
        if (volume > 0.02) {
            this._roar ??= SoundManager.play("wildfire_roar", { loop: true, ambient: true, dynamic: true, layer: Layer.Ground, falloff: 0, maxRange: 1, volume });
            this._roar.volume = volume;
        } else if (this._roar) {
            this._roar.stop();
            this._roar = undefined;
        }
        this._fireLight(insideShare, near);
    }

    /** inside the fire the screen's edges glow (the fire's vignette, as when you burn), warmer the deeper */
    private _fireLight(insideShare: number, near: number): void {
        const level = Math.max(insideShare * 0.8, near * 0.25);
        const shown = level.toFixed(2);
        if (shown === this._amberShown) return;
        this._amberShown = shown;
        if (!this._amber) {
            this._amber = document.createElement("div");
            this._amber.id = "wildfire-light";
            underHud(this._amber);
        }
        this._amber.style.opacity = shown;
        this._amber.classList.toggle("on", level > 0.005);
    }

    private _hideAll(): void {
        this._hideFrom(this._edge, 0);
        this._hideFrom(this._inside, 0);
        if (this._insideDust) { this._insideDust.style.opacity = "0"; this._insideDust.classList.remove("on"); }
        for (const owner of this._lampOwners) NightLight.setLamp(owner, undefined);
        if (this._amber) { this._amber.style.opacity = "0"; this._amber.classList.remove("on"); this._amberShown = ""; }
    }

    /** a match ended: everything gone */
    reset(): void {
        this._hideAll();
        for (const weed of this._tumbleweeds) weed.sprite.destroy();
        this._tumbleweeds = [];
        this._roar?.stop();
        this._roar = undefined;
        this._wind_sound?.stop();
        this._wind_sound = undefined;
        this._amber?.remove();
        this._amber = undefined;
        this._amberShown = "";
        for (const edge of this._edge) edge.sprite.destroy();
        for (const edge of this._inside) edge.sprite.destroy();
        this._edge.length = 0;
        this._inside.length = 0;
        this._insideDust?.remove();
        this._insideDust = undefined;
        this._layer?.destroy({ children: true });
        this._layer = undefined;
        this._hazard = "gas";
        this._windSeed = -1;
    }
}

export const HazardRender = new HazardRenderClass();

/**
 * the arcs of a circle (its middle `center`, `radius`) that lie inside the box `min` to `max`, as angle ranges [from,
 * to) (radians, `to` past `from`): the circle's crossings of the box's four sides cut it into arcs, and an arc whose
 * middle lies in the box is in it; no crossing at all, the whole circle is in the box or none of it is
 */
export function circleArcs(center: Vector, radius: number, min: Vector, max: Vector): Array<readonly [number, number]> {
    const crossings: number[] = [];
    const cross = (fixed: number, axis: "x" | "y", lo: number, hi: number): void => {
        const d = fixed - (axis === "x" ? center.x : center.y);
        if (Math.abs(d) > radius) return;
        const h = Math.sqrt(Math.max(0, radius * radius - d * d));
        const other = (axis === "x" ? center.y : center.x);
        for (const sign of [-1, 1]) {
            const value = other + sign * h;
            if (value < lo || value > hi) continue;
            const x = axis === "x" ? fixed : value, y = axis === "x" ? value : fixed;
            crossings.push(Math.atan2(y - center.y, x - center.x));
        }
    };
    cross(min.x, "x", min.y, max.y);
    cross(max.x, "x", min.y, max.y);
    cross(min.y, "y", min.x, max.x);
    cross(max.y, "y", min.x, max.x);
    const inBox = (angle: number): boolean => {
        const x = center.x + Math.cos(angle) * radius, y = center.y + Math.sin(angle) * radius;
        return x >= min.x && x <= max.x && y >= min.y && y <= max.y;
    };
    if (crossings.length === 0) return inBox(0) ? [[0, Math.PI * 2]] : [];
    const sorted = [...new Set(crossings.map(a => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)))].sort((a, b) => a - b);
    const arcs: Array<readonly [number, number]> = [];
    for (let i = 0; i < sorted.length; i++) {
        const from = sorted[i];
        const to = i + 1 < sorted.length ? sorted[i + 1] : sorted[0] + Math.PI * 2;
        if (to - from < 1e-6) continue;
        if (inBox((from + to) / 2)) arcs.push([from, to]);
    }
    return arcs;
}

/** the night's light reach of a burning line (tests): the lamps along it light the ground as a street light does */
export const WILDFIRE_LIGHT = Object.freeze({ step: FIRE_LAMP_STEP, radius: FIRE_LAMP_RADIUS, fireLight: Night.fireLight });
